import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import axios from 'axios';
import { useGetProfile, type UserInfo } from '../../../hooks/useGetProfile';
import { useUpdateProfile, type UpdatedUserInfo } from '../../../hooks/useUpdateProfile';

type ProfileField = keyof UpdatedUserInfo;

/**
 * Buffers edits to the current user's profile so a section can show the shared
 * save/discard bar instead of writing on every keystroke.
 *
 * Profile and Notifications are separate sections but one document, so each
 * keeps its own draft and sends only the fields it changed. `POST
 * /profile/update` patches at the field level, so the two cannot clobber each
 * other.
 */
export const useProfileDraft = () => {
    const { data, isLoading, isError, error } = useGetProfile();
    const updateProfile = useUpdateProfile();
    const [changed, setChanged] = useState<UpdatedUserInfo>({});
    const toast = useToast();

    const profile = useMemo<UserInfo | undefined>(
        () => (data ? { ...data, ...changed } : undefined),
        [data, changed],
    );

    /**
     * Records an edit, dropping it again if the value returns to what the
     * server has. `isEqual` is needed for the fields whose values are arrays or
     * dictionaries rather than scalars.
     */
    const setField = useCallback(
        <K extends ProfileField>(
            field: K,
            value: UpdatedUserInfo[K],
            isEqual?: (a: UpdatedUserInfo[K], b: UpdatedUserInfo[K]) => boolean,
        ) => {
            setChanged((prev) => {
                const next = { ...prev };
                const original = data?.[field as keyof UserInfo] as UpdatedUserInfo[K];
                const matchesServer = isEqual
                    ? isEqual(value, original)
                    : value === original;
                if (matchesServer) {
                    delete next[field];
                } else {
                    next[field] = value;
                }
                return next;
            });
        },
        [data],
    );

    const isDirty = Object.keys(changed).length > 0;

    const save = useCallback(async () => {
        if (!isDirty) return;
        try {
            await updateProfile.mutateAsync(changed);
            setChanged({});
            toast({
                title: 'Changes saved',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (err) {
            const serverMessage = axios.isAxiosError(err)
                ? err.response?.data?.message
                : undefined;
            toast({
                title: 'Failed to save changes',
                description: serverMessage || 'An unexpected error occurred.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    }, [changed, isDirty, toast, updateProfile]);

    const discard = useCallback(() => setChanged({}), []);

    return {
        profile,
        isLoading,
        isError,
        error,
        isDirty,
        isSaving: updateProfile.isPending,
        setField,
        save,
        discard,
    };
};
